import ExpeditionChapterHeading from "./ExpeditionChapterHeading";
import IconLink from "./IconLink";
import { siteUrl } from "./site";
import { expeditionLinks } from "./expeditionLinks";

const stateMachineCode = `// PlayerStateMachineCom::BeginPlay에서 월드 상태를 등록합니다. (발췌)
Add_State(PlayerState::Idle, Player_Idle::Create(static_pointer_cast<Player_WorldBase>(_owner.lock())));
Add_State(PlayerState::Move, Player_Move::Create(static_pointer_cast<Player_WorldBase>(_owner.lock())));
Add_State(PlayerState::Jump, Player_Jump::Create(static_pointer_cast<Player_WorldBase>(_owner.lock())));
Add_State(PlayerState::Parkour, Player_ParkourAction::Create(static_pointer_cast<Player_WorldBase>(_owner.lock())));

// StateMachineCom::Change_State의 전환 처리입니다. (발췌)
auto iter = _states.find(stateName);
if (iter == _states.end())
    return false;

if (_currentStateName == stateName || _currentState == iter->second)
    return true;

// 대상 상태의 진입 처리가 성공해야 기존 상태를 종료합니다.
if (!iter->second->EnterInvoke())
    return false;

if (_currentState != nullptr)
    _currentState->ExitInvoke();

_currentState = iter->second;
_currentStateName = stateName;
return true;`;

const prepareStateCode = `// Player_Jump의 파쿠르 전환 처리에서 발췌했습니다.
// Ray 판정으로 이동 요청을 준비한 뒤 상태를 전환합니다.
if (!parkourAction->Try_PrepareAction(actionType))
{
    return false;
}

const bool changed = stateCom->Change_State(PlayerState::Parkour);
if (!changed)
    parkourAction->Clear_PreparedAction();

return changed;`;

const presetCode = `// AnimState_Parser: 상태에 지정한 클립 이름을 실제 인덱스로 연결합니다.
for (uint32 i = 0; i < static_cast<uint32>(originalAnimations.size()); ++i)
{
    string availableName = originalAnimations[i]->Get_AnimationName();
    if (availableName == targetAnimName)
    {
        outStateAnimIndices[stateIdx] = i;
        mappedCount++;
        found = true;
        break;
    }
}

// ModelCom::Set_AnimationByState의 인덱스 조회 부분입니다.
if (stateIndex >= _stateAnimIndices.size()
    || _stateAnimIndices[stateIndex] == UINT32_MAX)
{
    return false;
}

const uint32 animIndex = _stateAnimIndices[stateIndex];`;

const rootRatioCode = `float ModelCom::Get_RootMotionTranslationRatio() const
{
    if (_animations.empty() || _rootNodeIndex == -1)
        return 0.f;

    // 블렌딩 중에는 전환 대상 애니메이션을 기준으로 계산합니다.
    uint32 activeAnimIndex = _isBlending ? _nextAnimIndex : _currentAnimIndex;
    if (activeAnimIndex >= _animations.size())
        return 0.f;

    const auto& curAnim = _animations[activeAnimIndex];
    // 첫 프레임에서 마지막 프레임까지의 루트 변위 크기입니다.
    float totalDist = curAnim->Get_RootTranslation(_rootNodeIndex).Length();
    if (totalDist <= 0.001f)
        return 0.f;

    // 첫 프레임을 기준으로 현재 루트가 이동한 변위입니다.
    Vec3 currentTranslation = curAnim->Get_CurrentRootTranslation(_rootNodeIndex);
    float currentDist = currentTranslation.Length();

    return clamp(currentDist / totalDist, 0.f, 1.f);
}`;

const pathMotionCode = `// Player_ParkourAction::Update의 이동 부분에서 발췌했습니다.
const float currentRatio = model->Get_RootMotionTranslationRatio();
float moveRatio = currentRatio - _prevRootMotionRatio;
if (moveRatio < 0.f)
    moveRatio = 0.f;

_prevRootMotionRatio = currentRatio;

if (remain > 0.01f && moveRatio > 0.f)
{
    // 애니메이션의 이동 비율을 실제 시작점 → 목표점 경로에 적용합니다.
    const Vec3 path = _request.targetPoint - _moveStartPos;
    Vec3 moveDelta = path * moveRatio;

    if (moveDelta.Length() > remain)
        moveDelta = toTarget;

    transform->Set_WorldPosition(currentPos + moveDelta);
    transform->LookAt(transform->Get_WorldPosition() + _request.faceDir);
    _movementCom->Sync_RuntimePositionToTransform();
}`;

const parkourRayCode = `// Player_ParkourAction::Build_ParkourRequestFromProbe에서 발췌했습니다.
// 전방 Ray로 환경 충돌점을 찾습니다.
PhysXQueryHit frontHit{};
if (!GAME->Raycast_Environment(frontOrigin, forward, frontDistance, frontHit) ||
    !Is_EnvironmentHit(frontHit))
{
    return false;
}

// 충돌점 안쪽의 위에서 아래로 Ray를 쏴 올라설 표면을 찾습니다.
Vec3 topProbeOrigin = frontHit.position + forward * kParkourTopProbeForwardOffset;
topProbeOrigin.y = frontOrigin.y + kParkourTopProbeUp;

PhysXQueryHit topHit{};
if (!GAME->Raycast_Environment(topProbeOrigin, Vec3(0.f, -1.f, 0.f), kParkourTopProbeDownDistance, topHit) ||
    !Is_EnvironmentHit(topHit))
{
    return false;
}

// 이후 높이 조건을 검사한 뒤, 상태에서 사용할 이동 요청을 구성합니다.
// ...
outRequest.startPoint = start;
outRequest.targetPoint = topHit.position;
outRequest.faceDir = forward;
outRequest.actionType = actionType;`;

function SourceLink({ path }: { path: string }) {
  return <IconLink href={`${expeditionLinks.source}/blob/main/${path}`} target="_blank" rel="noopener noreferrer">전체 코드 보기 ↗</IconLink>;
}

export default function ExpeditionCharacterMotion() {
  return <section className="expedition-case expedition-pipeline expedition-character" id="interaction" aria-labelledby="expedition-character-title">
    <div className="container">
      <header className="expedition-pipeline-header">
        <ExpeditionChapterHeading number="02" label="CHARACTER / STATE MACHINE" id="expedition-character-title" title="State Machine과 Animation Preset" technology="C++ · State 패턴 · 상속 · Enum 기반 애니메이션 매핑 · JSON" />
        <div className="expedition-pipeline-lead"><p>모든 캐릭터가 사용하는 공통 State Machine 구조를 구성했습니다. 캐릭터별 행동을 상태로 나누고 진입·갱신·종료 처리를 구현하면, State Machine이 상태의 등록과 전환, 현재 상태의 갱신을 담당합니다.</p><p>이를 돕는 Animation Preset도 모든 캐릭터가 사용하는 공통 기능입니다. 상태 코드는 필요한 동작을 Enum 값으로 요청하고, 개발자는 Preset에서 그 값에 대응하는 클립을 지정합니다. 저는 이 공통 구조를 제공하고, 월드 캐릭터에서는 일반 이동부터 지형 상호작용까지 대부분의 동작 로직을 직접 구현했습니다.</p></div>
      </header>

      <div className="expedition-pipeline-block" id="character-state">
        <div><h4>State Machine 구성</h4><p className="expedition-pipeline-stage-tech">공통 기반: State · StateMachineCom<br />캐릭터별 상속 · 상태 등록</p></div>
        <div className="expedition-pipeline-copy"><p><code>State</code>는 진입·갱신·종료 인터페이스를, <code>StateMachineCom</code>은 상태 객체의 등록·갱신·전환 기능을 제공합니다. 각 캐릭터는 이 기반을 상속해 필요한 상태와 동작을 구성합니다.</p>
          <div className="expedition-character-flow" aria-label="State Machine의 등록·갱신·전환 책임"><div><strong>상태 등록</strong><span>캐릭터별 상태 식별자와<br />상태 객체를 연결</span></div><div><strong>현재 상태 갱신</strong><span>Update · LateUpdate로<br />행동과 전환 조건 처리</span></div><div><strong>상태 전환 요청</strong><span>Change_State로 대상 조회<br />진입 성공 후 현재 상태 교체</span></div></div>
          <p>각 상태에서는 입력이나 실행 조건을 확인해 다음 상태로의 전환을 요청합니다. 전환 함수는 대상 상태의 <code>EnterInvoke</code>가 성공하면 기존 상태의 <code>ExitInvoke</code>를 호출하고 현재 상태를 교체합니다. 진입이 거절되면 기존 상태를 유지합니다.</p>
          <p>직접 구현한 월드 캐릭터에서는 <code>Player_State</code>에 입력·이동·모델 참조와 공통 전환 조건을 모으고, PlayerStateMachineCom에 개별 상태를 등록했습니다. Idle·Move·Jump·Parkour 등의 상태 안에서 이동, 애니메이션 선택, 입력 제한과 종료 후 복귀를 처리했습니다. Move는 걷기·달리기·질주를 선택하고, Parkour는 준비된 이동 요청을 수행한 뒤 Move 또는 Idle로 돌아갑니다.</p>
          <details className="expedition-evidence"><summary>상태 등록과 전환 코드 보기</summary><figure className="expedition-pipeline-code"><figcaption>상태 객체 등록 → 대상 진입 → 기존 상태 종료 → 현재 상태 교체</figcaption><pre><code>{stateMachineCode}</code></pre><div className="expedition-pipeline-code-footer"><p>Client의 PlayerStateMachineCom::BeginPlay와 StateMachineCom::Change_State에서 발췌했습니다. 대상 상태의 진입 처리를 먼저 호출하는 순서도 실제 구현에 맞췄습니다.</p></div></figure></details>
        </div>
      </div>

      <div className="expedition-pipeline-block" id="character-preset">
        <div><h4>상태의 애니메이션 연결<br className="expedition-pipeline-heading-break" /> Animation Preset</h4><p className="expedition-pipeline-stage-tech">사용자 지정 Enum · JSON<br />AnimState_Parser · ModelCom</p></div>
        <div className="expedition-pipeline-copy"><p>각 상태에서 사용할 애니메이션은 Animation Preset으로 연결했습니다. 에디터에서 개발자가 정의한 애니메이션 Enum을 선택하고 항목마다 클립을 지정하면 <code>.animmap.json</code>으로 저장합니다. 상태 코드는 클립 이름이나 배열 순서 대신 Enum 값으로 애니메이션을 요청합니다.</p>
          <figure className="expedition-character-preset"><div><span>Move 상태에서 동작 선택</span><code>PlayerState::Move<br />↓ 달리기 선택<br />Pl_Run</code></div><div><span>Animation Preset 조회</span><code>Pl_Run → 클립 이름</code></div><div><span>ModelCom에서 클립 재생</span><code>캐릭터별 달리기 클립</code></div><figcaption>행동 상태인 Move와 애니메이션 항목인 Pl_Run은 서로 다른 Enum입니다. 하나의 Move 상태에서 걷기·달리기·질주 항목을 선택하고, Preset이 각 항목을 실제 클립에 연결합니다.</figcaption></figure>
          <p>런타임에서는 AnimState_Parser가 저장된 상태와 클립 이름을 읽어, 상태 값에 대응하는 애니메이션 인덱스 배열을 구성합니다. <code>Set_AnimationByState</code>는 이 배열을 조회해 실제 클립을 선택합니다. 상태에서 클립의 배열 순서를 직접 지정하지 않고도, 캐릭터마다 필요한 애니메이션 세트를 구성할 수 있게 했습니다.</p>
          <p>State Machine이 행동과 전환을 담당하고, Preset은 그 행동에 필요한 애니메이션 선택을 돕습니다. 모든 캐릭터가 이 공통 기능을 사용하며, 각 담당자는 자신이 정의한 애니메이션 세트를 상태 코드에 연결합니다. 위 도식은 제가 구현한 월드 캐릭터의 적용 예입니다.</p>
          <details className="expedition-evidence"><summary>Preset 연결과 상태별 클립 조회 코드 보기</summary><figure className="expedition-pipeline-code"><figcaption>상태 값 → 모델의 애니메이션 인덱스</figcaption><pre><code>{presetCode}</code></pre><div className="expedition-pipeline-code-footer"><p>로더는 클립 이름으로 인덱스를 연결하고, 재생 시에는 상태 값으로 조회합니다. 연결되지 않은 상태는 재생 요청에서 걸러냅니다. 두 함수의 관련 부분을 발췌하고 설명 주석을 덧붙였습니다.</p><SourceLink path="Engine/Private/AnimState_Parser.cpp" /><br /><SourceLink path="Engine/Private/ModelCom.cpp" /></div></figure></details>
        </div>
      </div>

      <header className="expedition-character-application"><h4>월드 캐릭터 로직 구현</h4><p>공통 State Machine과 Preset을 바탕으로 월드 캐릭터 로직 대부분을 구현했습니다. 일반 이동과 상태 전환부터 파쿠르·로프·그래플링의 판정, 이동, 입력 제한과 종료 처리까지 연결했습니다. 아래에서는 그중 파쿠르의 목표 위치 판정과 루트 이동 적용을 예로 설명합니다.</p><figure className="expedition-frame"><IconLink href={siteUrl('/images/expedition-33/world.jpg')} target="_blank" rel="noopener noreferrer"><img src={siteUrl('/images/expedition-33/world.jpg')} alt="목조 구조물 위에서 이동하는 월드 플레이어" width="1280" height="720" loading="lazy" /></IconLink><figcaption>개인 발표 영상의 월드 탐험 장면.</figcaption></figure></header>
      <div className="expedition-pipeline-block" id="character-root-ratio">
        <div><h4>루트 이동 진행 비율과<br className="expedition-pipeline-heading-break" /> 실제 경로 적용</h4><p className="expedition-pipeline-stage-tech">Root Motion · 변위 비율<br />ModelCom → Client 이동 처리</p></div>
        <div className="expedition-pipeline-copy"><p>애니메이션에 기록된 이동 거리와 월드에서 이동해야 할 거리는 상황마다 다릅니다. 루트 이동을 실제 시작점과 목표점 사이에 적용할 수 있도록, ModelCom에서 루트 이동의 진행 비율을 계산해 제공했습니다.</p>
          <div className="expedition-character-formula" aria-label="루트 이동 진행 비율과 프레임 이동량 계산"><p><span>루트 이동 진행 비율</span><strong>시작 → 현재 루트 변위의 크기<br /><span className="expedition-character-divider">시작 → 마지막 루트 변위의 크기</span></strong></p><p><span>이번 프레임 이동량</span><strong>(목표점 − 시작점) × 진행 비율의 증가분</strong></p></div>
          <p>진행 비율은 첫 프레임 기준의 현재 루트 변위와 마지막 프레임까지의 변위를 비교해 0~1로 계산합니다. 블렌딩 중에는 전환 대상 애니메이션을 기준으로 삼습니다. Client는 이 값을 받아 실제 이동 경로에 적용하므로, 원본 루트 이동의 진행에 맞춰 캐릭터를 이동시킬 수 있습니다.</p>
          <p>파쿠르에서는 이전 프레임과 현재 프레임의 비율 차이에 시작점부터 목표점까지의 경로를 곱했습니다. 계산한 이동량이 남은 거리보다 크면 목표점까지의 변위로 제한하고, 적용한 위치를 MovementCom에도 동기화했습니다. 로프에서 빠져나오는 종료 동작에도 같은 진행 비율을 활용했습니다.</p>
          <details className="expedition-evidence"><summary>루트 이동 비율 계산과 파쿠르 적용 코드 보기</summary><figure className="expedition-pipeline-code"><figcaption>Engine에서 루트 이동 진행 비율 제공</figcaption><pre><code>{rootRatioCode}</code></pre><div className="expedition-pipeline-code-footer"><SourceLink path="Engine/Private/ModelCom.cpp" /></div></figure><figure className="expedition-pipeline-code"><figcaption>Client에서 실제 목표 경로에 적용</figcaption><pre><code>{pathMotionCode}</code></pre><div className="expedition-pipeline-code-footer"><p>Player_ParkourAction::Update 발췌. 진행 비율 계산과 이동 경로 적용을 나눠, 다른 캐릭터 담당자도 자신의 이동 처리에 비율을 사용할 수 있도록 했습니다.</p><SourceLink path="Client/Private/Player_ParkourAction.cpp" /></div></figure></details>
        </div>
      </div>

      <div className="expedition-pipeline-block" id="character-interaction">
        <div><h4>지형 판정과<br className="expedition-pipeline-heading-break" /> 상호작용 연결</h4><p className="expedition-pipeline-stage-tech">PhysX Raycast · 이동 요청<br />Parkour · Rope · Grappling</p></div>
        <div className="expedition-pipeline-copy"><p>파쿠르는 전방 Ray로 환경 충돌점을 찾고, 그 지점의 안쪽 위에서 아래로 다시 Ray를 쏴 올라설 표면을 구했습니다. 낮은 파쿠르와 높은 파쿠르의 판정 높이를 나누고, 판정 결과로 시작점·목표점·바라볼 방향·동작 종류를 묶은 이동 요청을 구성했습니다.</p>
          <figure className="expedition-character-ray"><svg viewBox="0 0 600 240" role="img" aria-labelledby="character-ray-title character-ray-desc"><title id="character-ray-title">파쿠르 목표 위치를 찾는 두 Ray의 설명도</title><desc id="character-ray-desc">플레이어 앞의 벽을 전방 Ray로 찾고, 충돌점 안쪽 위에서 아래로 쏜 Ray가 만난 표면을 목표 위치로 사용합니다.</desc><defs><marker id="character-ray-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" /></marker></defs><path d="M 35 190 H 300 V 105 H 560" className="expedition-character-terrain" /><rect x="78" y="110" width="42" height="80" rx="3" className="expedition-character-player" /><path d="M 122 120 H 300 M 325 55 V 105" className="expedition-character-ray-line" markerEnd="url(#character-ray-arrow)" /><path d="M 300 120 H 325 V 55" className="expedition-character-probe-offset" /><circle cx="325" cy="105" r="5" className="expedition-character-target" /><text x="164" y="104">전방 Ray</text><text x="345" y="57">상단 Ray</text><text x="345" y="128">목표 위치</text><text x="62" y="220">시작 위치</text></svg><figcaption>Ray 판정으로 목표 위치를 구하는 흐름을 정리한 설명도.</figcaption></figure>
          <p>파쿠르 상태는 준비된 요청을 받아 애니메이션을 선택하고, 앞에서 계산한 루트 이동 진행 비율로 목표점까지 이동합니다. 지형에서 이동할 위치를 구하는 처리와, 애니메이션을 재생하며 그 위치로 이동하는 처리를 나눴습니다.</p>
          <details className="expedition-evidence"><summary>Ray 판정과 파쿠르 상태 진입 코드 보기</summary><figure className="expedition-pipeline-code"><figcaption>환경 판정 결과를 파쿠르 이동 요청으로 구성</figcaption><pre><code>{parkourRayCode}</code></pre><div className="expedition-pipeline-code-footer"><p>Player_ParkourAction의 판정과 요청 구성 부분 발췌. 중간의 높이 검사는 생략했습니다.</p><SourceLink path="Client/Private/Player_ParkourAction.cpp" /></div></figure><figure className="expedition-pipeline-code"><figcaption>요청 준비 후 상태 전환</figcaption><pre><code>{prepareStateCode}</code></pre><div className="expedition-pipeline-code-footer"><p>Player_Jump 발췌. 준비에 실패하면 전환하지 않고, 전환이 거절되면 준비한 요청을 정리합니다.</p></div></figure></details>
          <p>로프 오르내리기와 그래플링은 월드 상호작용 지점이 제공하는 요청을 받아 각각의 상태로 연결했습니다. 공통 State Machine과 Animation Preset은 모든 캐릭터에서 사용하고, 월드 캐릭터에서는 이 구조를 바탕으로 대부분의 로직을 직접 구현했습니다. 루트 이동 진행 비율도 다른 캐릭터 담당자가 자신의 이동 처리에 활용하도록 제공했습니다.</p>
          <IconLink className="expedition-pipeline-next expedition-character-next" href="#modifier">Modifier 구조 →</IconLink>
        </div>
      </div>
    </div>
  </section>;
}
